import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { cx } from "../utils/cx";

export interface MenuItem {
  id: string;
  label: string;
  separatorBefore?: boolean;
  icon?: ReactNode;
  shortcut?: readonly string[];
  disabled?: boolean;
  tone?: "default" | "danger";
  onSelect: () => void;
}

export interface MenuProps {
  label: string;
  items: readonly MenuItem[];
  trigger?: ReactNode;
  align?: "start" | "end";
  className?: string;
}

export function Menu({
  label,
  items,
  trigger = "More",
  align = "end",
  className,
}: MenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open || !surfaceRef.current || !triggerRef.current) return;
    const surface = surfaceRef.current;
    surface.showPopover?.();
    const place = () => {
      const anchor = triggerRef.current!.getBoundingClientRect();
      Object.assign(surface.style, { position: "fixed", margin: "0", right: "auto", bottom: "auto", maxWidth: "calc(100vw - 16px)", maxHeight: "calc(100dvh - 16px)", overflowY: "auto" });
      const bounds = surface.getBoundingClientRect();
      const left = align === "start" ? anchor.left : anchor.right - bounds.width;
      surface.style.left = `${Math.max(8, Math.min(left, window.innerWidth - bounds.width - 8))}px`;
      surface.style.top = `${Math.max(8, Math.min(anchor.bottom + 6, window.innerHeight - bounds.height - 8))}px`;
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      surface.hidePopover?.();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !rootRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [open]);

  const focusItem = (index: number) => {
    const enabled = Array.from(
      rootRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]:not(:disabled)',
      ) ?? [],
    );
    enabled[(index + enabled.length) % enabled.length]?.focus();
  };

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const enabled = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]:not(:disabled)',
      ),
    );
    const current = enabled.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusItem(current + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusItem(current - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusItem(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusItem(enabled.length - 1);
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const openAndFocus = () => {
    setOpen(true);
    window.requestAnimationFrame(() => focusItem(0));
  };

  return (
    <div ref={rootRef} className={cx("ui-menu", className)}>
      <button
        ref={triggerRef}
        type="button"
        className="ui-button ui-button--secondary ui-button--medium"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (open) setOpen(false);
          else openAndFocus();
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            openAndFocus();
          }
        }}
      >
        {trigger}
      </button>
      {open ? (
        <div
          ref={surfaceRef}
          {...{ popover: "manual" }}
          role="menu"
          aria-label={label}
          className={`ui-menu__surface ui-menu__surface--${align}`}
          onKeyDown={handleMenuKeyDown}
        >
          {items.map((item) => (
            <Fragment key={item.id}>
              {item.separatorBefore ? (
                <div role="separator" className="ui-menu__separator" />
              ) : null}
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={cx(
                  "ui-menu__item",
                  item.tone === "danger" && "ui-menu__item--danger",
                )}
                onClick={() => {
                  if (item.disabled) return;
                  setOpen(false);
                  item.onSelect();
                  triggerRef.current?.focus();
                }}
              >
                {item.icon ? <span aria-hidden="true">{item.icon}</span> : null}
                <span className="ui-menu__label">{item.label}</span>
                {item.shortcut ? (
                  <span className="ui-menu__shortcut" aria-label={item.shortcut.join(" plus ")}>
                    {item.shortcut.map((key) => <kbd key={key}>{key}</kbd>)}
                  </span>
                ) : null}
              </button>
            </Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
}
